import type {
  ContentAudio,
  ContentImage,
  ContentText,
  ContentVideo,
} from "@tsmono/inspect-common/types";

/** Tool-result content usable as an action's before-state screenshot. */
export type ScreenshotContent =
  ContentText | ContentImage | ContentAudio | ContentVideo;

/** A visual browser action's arguments, narrowed for annotation rendering. */
export interface ToolAnnotation {
  action: string;
  coordinate?: [number, number] | undefined;
  text?: string | undefined;
  scrollDirection?: string | undefined;
}

/**
 * Tools whose actions can be annotated onto a screenshot. Membership requires
 * the tool's coordinate arguments to be in screenshot-pixel space and its
 * results to carry post-action screenshots (element-based browser tools like
 * web_browser_* don't qualify). The set plays two roles: it gates which calls
 * get a selfAnnotation, and the before-state scan in computeVisualActionContext
 * stops at the first tool call outside it — so adding a tool here also changes
 * which screenshots earlier actions may pair with.
 */
export const BROWSER_TOOL_FUNCTIONS = new Set(["browser", "computer"]);

export const CLICK_ACTIONS = new Set([
  "left_click",
  "right_click",
  "middle_click",
  "double_click",
  "triple_click",
]);

export const VISUAL_BROWSER_ACTIONS = new Set([
  ...CLICK_ACTIONS,
  "scroll",
  "type",
  "key",
]);

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asCoordinate(value: unknown): [number, number] | undefined {
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0]) &&
    typeof value[1] === "number" &&
    Number.isFinite(value[1])
  ) {
    return [value[0], value[1]];
  }
  return undefined;
}

/**
 * Narrows a tool call's arguments into a renderable annotation, or undefined
 * when the call is not a visual browser action (or its args are malformed).
 */
export function buildSelfAnnotation(
  functionName: string,
  args: Record<string, unknown>
): ToolAnnotation | undefined {
  if (!BROWSER_TOOL_FUNCTIONS.has(functionName)) return undefined;
  const action = asString(args.action);
  if (action === undefined || !VISUAL_BROWSER_ACTIONS.has(action))
    return undefined;
  return {
    action,
    coordinate: asCoordinate(args.coordinate),
    text: asString(args.text),
    scrollDirection: asString(args.scroll_direction),
  };
}
